import { CircleAlert, Package } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import type { RouterOutputs } from 'shared/api'
import { Badge } from 'shared/components/ui/badge'
import { Button } from 'shared/components/ui/button'
import { Input } from 'shared/components/ui/input'
import { Label } from 'shared/components/ui/label'
import { TableBody, TableCell, TableRow } from 'shared/components/ui/table'
import { useTranslate } from 'shared/lib/i18n'
import { cn } from 'shared/lib/utils'
import { readHubWeight } from '../lib/hub-weight'
import { useMustOpenWords, type BoxBanner } from '../lib/hub-boxes'
import { CompactTable, Hint, SectionTitle } from 'shared/components/section'
import { ToneBanner } from 'shared/components/tone-banner'

/**
 * Receiving by boxes at the hub — the Box's part of the batch checklist (spec §12,
 * §26; ADR-0047, ADR-0026, ADR-0025; tickets 11 + 29 + 55). A scanned Box is put on
 * the scales — the one weighing in the network, and it may be skipped — and then, in
 * one press, taken in whole or opened; there are no seals and no bench step. A MIX
 * Box, or one that may not ride on whole, is only ever opened. While a Box is opened
 * its Parcels are scanned one by one, unweighed, and «Завершить коробку» compares what
 * it held with what was expected. A weight beyond the partner's tolerance is answered
 * yellow — a Discrepancy for support; the intake goes on.
 *
 * Every call is injected; they default to the real tRPC client.
 */

type State = RouterOutputs['hubReceiving']['State']
type HubBoxCard = State['boxes'][number]

/** A Box's state in a word, with «вес вне допуска» beside it. */
function BoxStateBadge({ box }: { box: HubBoxCard }) {
	const { t } = useTranslate()
	return (
		<>
			<Badge variant='secondary' data-testid={`hub-box-state-${box.code}`} data-state={box.state}>
				{t(`hubBoxState.${box.state}`)}
			</Badge>
			{box.weightOutOfTolerance && (
				<Badge
					variant='outline'
					className='border-amber-500/60 text-amber-700 dark:text-amber-400'
					data-testid={`hub-box-flagged-${box.code}`}
				>
					{t('hubBox.flagged')}
				</Badge>
			)}
		</>
	)
}

/** What the bench sends with a Box act: the weight, when the scales gave one. */
export interface BoxWeighing {
	weightKg?: number
}

/**
 * A scanned Box: what it is, its weight (optional — empty means not weighed) and the
 * two acts in one press each — «Принять целиком» or «Вскрыть коробку». A MIX Box
 * shows only «Вскрыть». Enter in the weight moves to the first act, whose Enter
 * confirms.
 */
export function BoxForm({
	box,
	byBoxes,
	busy,
	onReceive,
	onOpen,
	onCancel,
}: {
	box: HubBoxCard
	byBoxes: boolean
	busy: boolean
	onReceive: (input: BoxWeighing) => void
	onOpen: (input: BoxWeighing) => void
	onCancel: () => void
}) {
	const { t } = useTranslate()
	const mustOpenWords = useMustOpenWords()
	const [weightText, setWeightText] = useState('')
	const [weightInvalid, setWeightInvalid] = useState(false)
	const weightRef = useRef<HTMLInputElement>(null)
	const receiveRef = useRef<HTMLButtonElement>(null)
	const openRef = useRef<HTMLButtonElement>(null)
	const mayTakeWhole = byBoxes && box.mustOpen.length === 0 && box.state === 'expected'
	const mayOpen = byBoxes && box.state === 'expected'

	useEffect(() => {
		if (mayOpen) weightRef.current?.focus()
	}, [mayOpen])

	/** The weight as typed: none when the field is empty, `null` when it is not a weight. */
	const weighing = (): BoxWeighing | null => {
		if (weightText.trim() === '') return {}
		const weightKg = readHubWeight(weightText)
		return weightKg === null ? null : { weightKg }
	}

	const act = (send: (input: BoxWeighing) => void) => {
		const input = weighing()
		if (input === null) {
			setWeightInvalid(true)
			weightRef.current?.focus()
			return
		}
		send(input)
	}

	return (
		<div className='flex flex-col gap-4 rounded-lg border bg-muted/30 p-5' data-testid='hub-box-form'>
			<div className='flex flex-col gap-1'>
				<div className='flex flex-wrap items-center gap-2'>
					<Package className='size-5 text-muted-foreground' aria-hidden />
					<span className='text-lg font-semibold'>{t('hubBox.scanned')}</span>
					<span className='font-mono text-lg font-semibold' data-testid='hub-box-code'>
						{box.code}
					</span>
					<BoxStateBadge box={box} />
				</div>
				<p className='text-sm text-muted-foreground' data-testid='hub-box-summary'>
					{t('hubBox.summary', {
						point: box.pointCode ?? t('hubBox.mix'),
						lines: String(box.lines),
						declared: box.declaredWeightKg === null ? '—' : String(box.declaredWeightKg),
					})}
					{box.refusedLines > 0 && ` · ${t('hubBox.refusedLines', { count: String(box.refusedLines) })}`}
				</p>
			</div>
			{!byBoxes && (
				<Warning testId='hub-box-per-piece'>{t('hubBox.perPiece')}</Warning>
			)}
			{byBoxes && box.mustOpen.length > 0 && mayOpen && (
				<Warning testId='hub-box-must-open'>
					{t('hubBox.mustOpen', { reasons: mustOpenWords(box.mustOpen) })}
				</Warning>
			)}
			{byBoxes && !mayOpen && (
				<p className='text-sm text-muted-foreground' data-testid='hub-box-done'>
					{t(`hubBox.already_${box.state}`)}
				</p>
			)}
			<div className='flex flex-wrap items-end gap-3'>
				{mayOpen && (
					<div className='grid w-56 gap-1.5'>
						<div className='flex items-center gap-1.5'>
							<Label htmlFor='hub-box-weight'>{t('hubBox.weight')}</Label>
							<Hint>{t('hubBox.weightHint')}</Hint>
						</div>
						<Input
							id='hub-box-weight'
							ref={weightRef}
							data-testid='hub-box-weight'
							inputMode='decimal'
							autoComplete='off'
							className='h-9 text-base'
							value={weightText}
							placeholder={t('hub.weightPlaceholder')}
							aria-invalid={weightInvalid || undefined}
							onChange={(e) => {
								setWeightText(e.target.value)
								setWeightInvalid(false)
							}}
							onKeyDown={(e) => {
								if (e.key !== 'Enter') return
								e.preventDefault()
								if (weighing() === null) {
									setWeightInvalid(true)
									return
								}
								const next = mayTakeWhole ? receiveRef : openRef
								next.current?.focus()
							}}
						/>
					</div>
				)}
				{mayTakeWhole && (
					<Button
						ref={receiveRef}
						type='button'
						size='lg'
						data-testid='hub-box-receive'
						disabled={busy}
						onClick={() => act(onReceive)}
					>
						{t('hubBox.receive')}
					</Button>
				)}
				{mayOpen && (
					<Button
						ref={openRef}
						type='button'
						size='lg'
						variant={mayTakeWhole ? 'outline' : 'default'}
						data-testid='hub-box-open'
						disabled={busy}
						onClick={() => act(onOpen)}
					>
						{t('hubBox.open')}
					</Button>
				)}
				<Button
					type='button'
					size='lg'
					variant='ghost'
					data-testid='hub-box-cancel'
					disabled={busy}
					onClick={onCancel}
				>
					{t('hub.cancel')}
				</Button>
			</div>
			{weightInvalid && (
				<p className='text-sm text-destructive' data-testid='hub-box-weight-invalid'>
					{t('hub.weightInvalid')}
				</p>
			)}
		</div>
	)
}

/** Why a Box cannot go the usual way, in amber. */
function Warning({ testId, children }: { testId: string; children: string }) {
	return (
		<p className='flex items-center gap-2 text-sm font-medium text-amber-700 dark:text-amber-300' data-testid={testId}>
			<CircleAlert className='size-4 shrink-0' aria-hidden />
			{children}
		</p>
	)
}

/** The Box being unpacked: its Parcels are scanned per piece now; «Завершить коробку». */
export function CurrentBoxBar({
	box,
	busy,
	onFinish,
	onLeave,
}: {
	box: HubBoxCard
	busy: boolean
	onFinish: () => void
	onLeave: () => void
}) {
	const { t } = useTranslate()
	return (
		<div
			className='flex flex-wrap items-center gap-3 rounded-lg border border-amber-500/60 bg-amber-500/10 px-4 py-2.5 text-amber-900 dark:text-amber-200'
			data-testid='hub-current-box'
			data-code={box.code}
		>
			<Package className='size-4 shrink-0' aria-hidden />
			<span className='font-medium'>
				{t('hubBox.unpacking', {
					code: box.code,
					done: String(box.receivedLines),
					total: String(box.lines),
				})}
			</span>
			<div className='ml-auto flex gap-2'>
				<Button type='button' size='sm' data-testid='hub-box-finish' disabled={busy} onClick={onFinish}>
					{t('hubBox.finish')}
				</Button>
				<Button type='button' size='sm' variant='outline' disabled={busy} onClick={onLeave}>
					{t('hubBox.leave')}
				</Button>
			</div>
		</div>
	)
}

/**
 * The partner's Boxes of the batch, each with its state, its weight and «вес вне допуска».
 * A press on a Box filters the checklist by it; a second press lifts the filter.
 */
export function BoxesList({
	boxes,
	selected,
	onSelect,
	onUnpack,
}: {
	boxes: readonly HubBoxCard[]
	selected: string | null
	onSelect: (code: string | null) => void
	onUnpack: (box: HubBoxCard) => void
}) {
	const { t } = useTranslate()
	if (boxes.length === 0) return null
	return (
		<section className='flex flex-col gap-2'>
			<SectionTitle title={t('hubBox.listTitle')} count={boxes.length} />
			<CompactTable testId='hub-boxes'>
				<TableBody>
					{boxes.map((box) => (
						<TableRow
							key={box.code}
							data-testid={`hub-box-${box.code}`}
							data-selected={box.code === selected || undefined}
							aria-selected={box.code === selected}
							className={cn(
								'cursor-pointer',
								box.code === selected && 'bg-primary/10 hover:bg-primary/15',
							)}
							onClick={() => onSelect(box.code === selected ? null : box.code)}
						>
							<TableCell className='align-top'>
								<div className='font-mono font-medium'>{box.code}</div>
								<div className='text-xs text-muted-foreground'>
									{box.pointCode ?? t('hubBox.mix')} · {box.receivedLines}/{box.lines}
								</div>
							</TableCell>
							<TableCell className='align-top'>
								<div className='flex flex-wrap gap-1'>
									<BoxStateBadge box={box} />
								</div>
								<div
									className={cn(
										'mt-1 text-xs text-muted-foreground',
										box.weightOutOfTolerance && 'text-amber-700 dark:text-amber-400',
									)}
								>
									{box.weightKg !== null ? t('hub.kg', { weight: String(box.weightKg) }) : '—'}
									{box.declaredWeightKg !== null &&
										` (${t('hubBox.declared', { weight: String(box.declaredWeightKg) })})`}
								</div>
							</TableCell>
							<TableCell className='w-px text-right align-top'>
								{box.state === 'opened' && (
									<Button
										type='button'
										size='xs'
										variant='outline'
										data-testid={`hub-box-unpack-${box.code}`}
										onClick={(e) => {
											e.stopPropagation()
											onUnpack(box)
										}}
									>
										{t('hubBox.unpack')}
									</Button>
								)}
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</CompactTable>
		</section>
	)
}

/** The lines refused when the batch was announced. */
export function RefusedLinesList({ refused }: { refused: State['refused'] }) {
	const { t } = useTranslate()
	if (refused.length === 0) return null
	return (
		<section className='flex flex-col gap-2'>
			<SectionTitle title={t('hubBox.refusedLinesTitle')} count={refused.length} hint={t('hubBox.refusedLinesHint')} />
			<CompactTable testId='hub-refused-lines'>
				<TableBody>
					{refused.map((r) => (
						<TableRow key={r.trackingCode}>
							<TableCell>
								<div className='font-mono'>{r.trackingCode}</div>
								<div className='text-xs text-muted-foreground'>
									{r.point}
									{r.boxCode && ` · ${r.boxCode}`}
								</div>
							</TableCell>
							<TableCell className='text-muted-foreground'>
								{t(`hubUpload.reason_${r.reason}`) || r.reason}
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</CompactTable>
		</section>
	)
}

/** The Box banner, in its colour. */
export function BoxAnswerBanner({ banner, since }: { banner: BoxBanner; since?: number }) {
	const { t } = useTranslate()
	return (
		<ToneBanner
			tone={banner.tone}
			title={banner.title}
			detail={banner.detail}
			testId='hub-box-answer'
			titleTestId='hub-box-answer-title'
			detailTestId='hub-box-answer-detail'
			attributes={{ 'data-outcome': banner.outcome }}
			since={since}
		>
			{banner.shortage && banner.shortage.length > 0 && (
				<div className='text-sm' data-testid='hub-box-shortage'>
					{t('hubBox.shortage')}: <span className='font-mono'>{banner.shortage.join(', ')}</span>
				</div>
			)}
			{banner.extras && banner.extras.length > 0 && (
				<div className='text-sm' data-testid='hub-box-extras'>
					{t('hubBox.extras')}: <span className='font-mono'>{banner.extras.join(', ')}</span>
				</div>
			)}
		</ToneBanner>
	)
}
